import { Fragment, useState } from "react";
import {
  approvedPlanCopy,
  type Billing,
  isPlanApproved,
  planApprovalCopy,
  usePlanApproval,
} from "./plan";
import {
  DROP_TABLE_BLOCK_HANDLE,
  dismissSetupDone,
  isSetupDoneDismissed,
  openSetupStep,
  type PlacementsRead,
  SETUP_STEP_TITLES,
  SETUP_STEPS,
  type SetupStepKey,
  setupGuideView,
} from "./setupSteps";

/**
 * How to add the optional Loot box block (`extensions/loot-theme/blocks/loot-box.liquid`), which
 * is not a step: on the drop table step, and on the guide once it folds.
 */
const LOOT_BOX_BLOCK_NOTE =
  "Optional: to feature a box on your homepage or any other page, open that page in the theme editor, click Add section, choose Loot box under Apps, pick the box in its Loot box product setting, and click Save.";

/** The progress bar's fills: a step done, in the success icon's green, and one still to do. */
const DONE_FILL = "#29845a";
const TRACK = "#d4d4d4";

/** A badge beside a step's title: Polaris's own tones, the two a step's state uses. */
interface StepBadge {
  label: string;
  tone: "info" | "warning";
}

/** The plan's state beside the step's title, once the merchant has asked for it. */
const PLAN_BADGE: Partial<Record<Billing["status"], StepBadge>> = {
  pending: { label: "Waiting for approval", tone: "info" },
  declined: { label: "Declined", tone: "warning" },
  cancelled: { label: "Cancelled", tone: "warning" },
  expired: { label: "Expired", tone: "warning" },
};

interface StepView {
  key: SetupStepKey;
  title: string;
  /** What the step is for. */
  body: string;
  /** What to click, in order, from the step's action to Save. */
  instructions?: string[];
  /** Something optional the merchant can do in the same editor. */
  note?: string;
  done: boolean;
  badge?: StepBadge;
  /** A link into Shopify's own editors, or the plan's approval. */
  action?: { label: string; href: string } | { label: string; onClick: () => void; busy: boolean };
}

/**
 * Home's setup guide, five steps in Shopify's own setup-guide layout without its card: approve
 * the Loot Boxes plan, turn on the app embed (the reveal banner) and add the drop table block to
 * the product template in the theme editor, and add the reveal button to the Thank-you page and to
 * the Order status page in the checkout editor. Creating a box waits on all five, so the guide is
 * what explains Home's
 * disabled "Create a box". Each placement says what to click, from its deep link to Save. The Loot
 * box block is not a step: merchants place box products wherever they like, so a dedicated
 * section is optional, and how to add one is a note on the drop table step and on the folded
 * guide. A step is done
 * once the plan is approved (active, or frozen, which the banner reports) or Shopify reports the
 * placement (`usePlacements`, read by `App` for every "Create a box" too); the bar shows which.
 * One step is open at a time, the first not done unless the merchant opens another. Once every
 * step is done the guide folds to one line, which the merchant can dismiss; a step undone later
 * (a cancelled plan, an embed switched off) brings the guide back.
 */
export function SetupChecklist({
  shopDomain,
  billing,
  placements,
}: {
  shopDomain: string;
  billing: Billing;
  placements: PlacementsRead;
}) {
  const approve = usePlanApproval();
  const [chosen, setChosen] = useState<SetupStepKey | "none" | null>(null);
  const [dismissed, setDismissed] = useState(() => isSetupDoneDismissed(shopDomain));

  // Nothing until Shopify answers: drawing the full list first made it flash, then vanish, for a
  // store already set up. A plan still to approve draws at once, since Home has no banner for it
  // and a slow read must not hide the approval. A failed or pending read shows every placement
  // as still to do, and a failed one says so, since it also keeps "Create a box" disabled.
  if (placements === "pending" && isPlanApproved(billing.status)) return null;
  const placed =
    typeof placements === "string"
      ? { appEmbed: false, dropTableBlock: false, thankYouBlock: false, orderStatusBlock: false }
      : placements;
  const done: Record<SetupStepKey, boolean> = { plan: isPlanApproved(billing.status), ...placed };
  const doneCount = SETUP_STEPS.filter((key) => done[key]).length;
  const view = setupGuideView(done, dismissed);

  if (view === "hidden") return null;
  if (view === "folded") {
    return (
      <s-stack direction="inline" gap="base" alignItems="center" justifyContent="space-between">
        <s-stack direction="inline" gap="small-200" alignItems="center">
          <s-badge tone="success" icon="check">
            Store set up
          </s-badge>
          <s-text color="subdued">
            The plan is approved, and the app embed, the product page drop table and the reveal
            button on the Thank-you and Order status pages are in place. {LOOT_BOX_BLOCK_NOTE}
          </s-text>
        </s-stack>
        <s-button
          variant="tertiary"
          icon="x"
          accessibilityLabel="Dismiss"
          onClick={() => {
            dismissSetupDone(shopDomain);
            setDismissed(true);
          }}
        />
      </s-stack>
    );
  }

  const admin = `https://${shopDomain}/admin`;
  const plan = isPlanApproved(billing.status)
    ? null
    : planApprovalCopy(billing.status, billing.feeBps);
  const steps: StepView[] = [
    {
      key: "plan",
      title: SETUP_STEP_TITLES.plan,
      body: plan ? plan.body : approvedPlanCopy(billing),
      done: done.plan,
      badge: PLAN_BADGE[billing.status],
      action: plan
        ? { label: plan.action, onClick: () => approve.mutate(), busy: approve.isPending }
        : undefined,
    },
    {
      key: "appEmbed",
      title: SETUP_STEP_TITLES.appEmbed,
      body: "Shows shoppers a banner when they have a box waiting to be revealed.",
      instructions: [
        "Click Open theme editor. Shopify opens App embeds with Loot Boxes banner switched on.",
        "Click Save.",
      ],
      done: done.appEmbed,
      action: {
        label: "Open theme editor",
        // Shopify's deep link format: `{api_key}/{block handle}`, the slash unencoded.
        href: `${admin}/themes/current/editor?context=apps&activateAppId=${shopify.config.apiKey}/app-embed`,
      },
    },
    {
      key: "dropTableBlock",
      title: SETUP_STEP_TITLES.dropTableBlock,
      body: "Adds the Loot box drop table block to your product template, so shoppers see every item a box can hold, and its odds, before they buy. It stays hidden on products that aren't loot boxes.",
      instructions: [
        "Click Add to product page. Shopify opens your default product template with the Loot box drop table block added beside the price and Add to cart.",
        "Move the block if you like, then click Save.",
        "If a loot box uses another product template, pick that template at the top of the theme editor, click Add block, choose Loot box drop table under Apps, and click Save.",
      ],
      note: LOOT_BOX_BLOCK_NOTE,
      done: done.dropTableBlock,
      action: {
        label: "Add to product page",
        // Into the default product template's main section, beside the price and Add to cart.
        href: `${admin}/themes/current/editor?template=product&addAppBlockId=${shopify.config.apiKey}/${DROP_TABLE_BLOCK_HANDLE}&target=mainSection`,
      },
    },
    {
      key: "thankYouBlock",
      title: SETUP_STEP_TITLES.thankYouBlock,
      body: "Adds a button to the Thank-you page, so a buyer can open their box right after checkout.",
      instructions: [
        "Click Open checkout editor. Shopify opens the checkout editor on the Thank-you page.",
        "Under Apps, add the Loot Boxes reveal block.",
        "Click Save.",
      ],
      done: done.thankYouBlock,
      action: {
        label: "Open checkout editor",
        href: `${admin}/settings/checkout/editor?page=thank-you&context=apps`,
      },
    },
    {
      key: "orderStatusBlock",
      title: SETUP_STEP_TITLES.orderStatusBlock,
      body: "Adds the same button to the Order status page, which View your order in your order confirmation email opens, so a buyer who left the Thank-you page can still open their box.",
      instructions: [
        "Click Open checkout editor. Shopify opens the checkout editor on the Order status page. If it opens another page, choose Order status from the page menu at the top of the editor.",
        "Under Apps, add the Loot Boxes reveal block.",
        "Click Save.",
      ],
      done: done.orderStatusBlock,
      action: {
        label: "Open checkout editor",
        href: `${admin}/settings/checkout/editor?page=order-status&context=apps`,
      },
    },
  ];
  const openKey = openSetupStep(done, chosen);

  return (
    <s-stack gap="base">
      <s-stack gap="small-200">
        <s-heading>Set up Loot Boxes</s-heading>
        <s-paragraph color="subdued">
          Finish these five steps to create and sell loot boxes.
        </s-paragraph>
      </s-stack>
      {placements === "failed" && (
        <s-banner tone="warning">
          Shopify didn't say which of these are in place, so they show as still to do and you can't
          create a box yet. Reload the app to check again.
        </s-banner>
      )}
      <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
        <SetupProgress done={SETUP_STEPS.map((key) => ({ key, done: done[key] }))} />
        <s-text color="subdued">
          {doneCount} of {SETUP_STEPS.length} done
        </s-text>
      </s-grid>
      <s-box border="base" borderRadius="base" background="base">
        {steps.map((step, i) => (
          <Fragment key={step.key}>
            {i > 0 && <s-divider />}
            <SetupStep
              step={step}
              number={i + 1}
              open={openKey === step.key}
              onToggle={() => setChosen(openKey === step.key ? "none" : step.key)}
            />
          </Fragment>
        ))}
      </s-box>
    </s-stack>
  );
}

/** One segment per step, in the guide's order, filled once that step is done. */
function SetupProgress({ done }: { done: { key: SetupStepKey; done: boolean }[] }) {
  return (
    <div
      aria-hidden
      style={{ display: "grid", gridTemplateColumns: `repeat(${done.length}, 1fr)`, gap: 4 }}
    >
      {done.map((step) => (
        <div
          key={step.key}
          style={{ height: 6, borderRadius: 999, background: step.done ? DONE_FILL : TRACK }}
        />
      ))}
    </div>
  );
}

/**
 * A step's row: whether it is done, its number and title, and a toggle; open, what it is for and
 * its action, in the subdued box Shopify's setup guide draws.
 */
function SetupStep({
  step,
  number,
  open,
  onToggle,
}: {
  step: StepView;
  number: number;
  open: boolean;
  onToggle: () => void;
}) {
  const action = step.action;
  return (
    <s-box>
      <s-grid
        gridTemplateColumns="auto 1fr auto"
        gap="small-300"
        alignItems="center"
        padding="small"
      >
        <s-icon
          type={step.done ? "check-circle" : "circle-dashed"}
          tone={step.done ? "success" : "neutral"}
        />
        <s-stack direction="inline" gap="small-200" alignItems="center">
          <s-text type="strong">
            {number}. {step.title}
          </s-text>
          {step.badge && <s-badge tone={step.badge.tone}>{step.badge.label}</s-badge>}
        </s-stack>
        <s-button
          variant="tertiary"
          icon={open ? "chevron-up" : "chevron-down"}
          accessibilityLabel={`${open ? "Hide" : "Show"} step ${number}, ${step.title}`}
          onClick={onToggle}
        />
      </s-grid>
      {open && (
        <s-box padding="small" paddingBlockStart="none">
          <s-box padding="base" background="subdued" borderRadius="base">
            <s-stack gap="base">
              <s-paragraph>{step.body}</s-paragraph>
              {step.instructions && (
                <s-ordered-list>
                  {step.instructions.map((line) => (
                    <s-list-item key={line}>{line}</s-list-item>
                  ))}
                </s-ordered-list>
              )}
              {step.note && <s-paragraph color="subdued">{step.note}</s-paragraph>}
              {action && (
                <s-stack direction="inline">
                  {"href" in action ? (
                    <s-button
                      href={action.href}
                      target="_top"
                      variant={step.done ? "secondary" : "primary"}
                    >
                      {action.label}
                    </s-button>
                  ) : (
                    <s-button
                      variant="primary"
                      loading={action.busy || undefined}
                      disabled={action.busy || undefined}
                      onClick={action.onClick}
                    >
                      {action.label}
                    </s-button>
                  )}
                </s-stack>
              )}
            </s-stack>
          </s-box>
        </s-box>
      )}
    </s-box>
  );
}
